import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useNavigate,
  Outlet,
} from "react-router-dom";

import { UserProvider, useUser } from "./components/shared/UserContext";
import { ClassroomModeProvider, useClassroomMode } from "./components/shared/classroomMode";
import { getAuth, onAuthStateChanged } from "firebase/auth";

// Components
import NavBar from "./components/shared/NavBar";
import JoinRequestWatcher from "./components/shared/JoinRequestWatcher";
import ConnectionWatcher from "./components/shared/ConnectionWatcher";
import ScrollToTop from "./components/shared/ScrollToTop";
import AnalyticsTracker from "./components/shared/AnalyticsTracker";
import { TourProvider } from "./components/shared/GuidedTour";
import ClassroomNavBar from "./components/shared/ClassroomNavBar";
import LogInForm from "./components/landing/LogInForm";
import RegistrationForm from "./components/landing/RegistrationForm";
import VerifyEmail from "./components/landing/VerifyEmail";
import Dashboard from "./components/teacher/Dashboard";
import MobileDrawer from "./components/teacher/MobileDrawer";
import Home from "./components/landing/Home";
import About from "./components/landing/About";
import { PrivacyPolicy, TermsOfService, Support } from "./components/landing/LegalPages";
import ClassSignOutPage from "./components/teacher/ClassSignOutPage";
import PublicSignOutPage from "./components/landing/PublicSignOutPage";
import AddClass from "./components/teacher/AddClass";
import DisplayClasses from "./components/teacher/ClassList";
import ImportClasses from "./components/teacher/ImportClasses";
import DisplayEnrolledStudents from "./components/teacher/EnrolledStudents";
import Attendance from "./components/teacher/Attendance";
import AttendanceRecord from "./components/teacher/AttendanceRecord";
import SignOutsTeacherRecord from "./components/teacher/SignOutHistory";
import EmergencyView from "./components/teacher/EmergencyView";
import EmergencyBanner from "./components/shared/EmergencyBanner";
import Invitations from "./components/teacher/Invitations";
import SchoolList from "./components/teacher/SchoolList";
import OnboardingWizard from "./components/landing/OnboardingWizard";
import ArchivedClasses from "./components/teacher/ArchivedClasses";
import Settings from "./components/teacher/Settings";
import AdminPanel from "./components/teacher/AdminPanel";
import ErrorBoundary from "./components/shared/ErrorBoundary";
import AttendanceViewer from "./components/teacher/AttendanceViewer";
import StudentLogIn from "./components/student/StudentLogIn"; // Student Login
import StudentNavBar from "./components/student/StudentNavBar"; // Student Navigation Bar
import StudentDashboard from "./components/student/StudentDashboard";
import LendingPage from "./components/teacher/LendingPage";
import LibraryPage from "./components/teacher/LibraryPage";
import SchoolView from "./components/teacher/SchoolView";
import SchoolAdmin from "./components/teacher/SchoolAdmin";
import SchoolRoster from "./components/teacher/SchoolRoster";
import StudentInsights from "./components/teacher/StudentInsights";
import ScramblePage from "./components/scramble/ScramblePage";
import ScrambleNew from "./components/scramble/ScrambleNew";

import "./App.css";

function App() {
  const [userLoggedIn, setUserLoggedIn] = useState(false);
  // Until Firebase reports the initial auth state, we don't yet know if the user
  // is signed in — so we must NOT bounce them to /loginform (that race is why a
  // logged-in user could land on the login page on a fresh load).
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUserLoggedIn(!!user);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  return (
    <Router>
      <UserProvider>
        <ClassroomModeProvider>
          <AppShell userLoggedIn={userLoggedIn} authLoading={authLoading} />
        </ClassroomModeProvider>
      </UserProvider>
    </Router>
  );
}

// True on students.classboon.com, or when forced on a localhost dev box via ?student=1.
function detectStudentsApp() {
  if (window.location.hostname.startsWith("students")) return true;
  const isLocal =
    window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  if (!isLocal) return false;
  const param = new URLSearchParams(window.location.search).get("student");
  if (param === "1") localStorage.setItem("cb_force_student", "1");
  if (param === "0") localStorage.removeItem("cb_force_student");
  return localStorage.getItem("cb_force_student") === "1";
}

// App shell: nav surfaces + route tree. Lives inside the providers so it can read
// Classroom Mode. Three parallel route trees: the students subdomain, a classroom-
// locked device, and the normal teacher app.
function AppShell({ userLoggedIn, authLoading }) {
  // Detect if we are on students.classboon.com. For local development the student
  // app can be forced on with `?student=1` (persisted for the session, localhost
  // only, so it never affects production) and turned off with `?student=0`.
  const isStudentsSubdomain = detectStudentsApp();
  // The standalone golf Scramble scorecard is a self-contained, account-free
  // surface — it renders without any ClassBoon nav/banner chrome (like the
  // student subdomain does), so friends opening a share link see just the game.
  const isScramble = window.location.pathname.startsWith("/scramble");
  const { classroomMode, enterClassroomMode } = useClassroomMode();
  const { classroomPin, needsOnboarding, user, emailVerified, hasProfile, loading } = useUser();
  // Classroom Mode only applies to a signed-in teacher device on the main app.
  const inClassroom = classroomMode && userLoggedIn && !isStudentsSubdomain;
  // A logged-in teacher whose email isn't verified is held on the VerifyEmail
  // screen — this is what keeps bot sign-ups from ever reaching the app or
  // generating data. Only applies to brand-new accounts: Google addresses arrive
  // verified, and existing users (who already have a profile) are grandfathered
  // in so nobody legacy gets locked out. `!loading` avoids flashing the screen
  // before the profile snapshot resolves.
  const mustVerifyEmail =
    !isStudentsSubdomain &&
    !inClassroom &&
    userLoggedIn &&
    !!user &&
    !emailVerified &&
    !hasProfile &&
    !loading;

  // Keyboard shortcut to lock the device into Classroom Mode: Ctrl/Cmd + Shift + L
  // (L = Lock). Guarded so you can't lock yourself out — requires a PIN to be set
  // first (the same PIN that exits). The classroom route tree's "*" redirect sends
  // you to Sign-Out automatically once locked.
  useEffect(() => {
    if (isStudentsSubdomain) return;
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || !e.shiftKey || e.key.toLowerCase() !== "l") return;
      e.preventDefault();
      if (!userLoggedIn || classroomMode) return;
      if (!classroomPin) {
        window.alert("Set a 4-digit Classroom PIN in Settings → Classroom first — you'll need it to exit Classroom Mode.");
        return;
      }
      if (window.confirm("Enter Classroom Mode? The device locks to kid-safe screens and needs your PIN to exit.")) {
        enterClassroomMode();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isStudentsSubdomain, userLoggedIn, classroomMode, classroomPin, enterClassroomMode]);

  // Hold unverified teachers on the verification screen — nothing else in the
  // authed app renders until they click the link.
  if (mustVerifyEmail) {
    return (
      <TourProvider>
        <div className="app-container">
          <VerifyEmail />
        </div>
      </TourProvider>
    );
  }

  return (
    <TourProvider>
    {/* On the standalone scramble surface there's no navbar, so drop the
        app-container's navbar spacing/centering (the `bare` modifier) to avoid
        an empty band at the top. */}
    <div className={`app-container${isScramble ? " app-container--bare" : ""}`}>
      {/* Reset scroll to the top on every route change */}
      <ScrollToTop />
      {/* Log a GA4 page_view on every route change (SPA nav isn't auto-tracked) */}
      <AnalyticsTracker />
      {/* Connectivity/auth watchdog — shown on every surface */}
      <ConnectionWatcher />
      {/* Nav: locked classroom navbar, or the full teacher navbar */}
      {!isStudentsSubdomain && !isScramble && (inClassroom ? <ClassroomNavBar /> : <NavBar />)}
      {/* Global mobile menu: the hamburger drawer works on every logged-in page,
          not just the routes that use the desktop AuthenticatedLayout sidebar. */}
      {!isStudentsSubdomain && !isScramble && !inClassroom && userLoggedIn && <MobileDrawer />}
      {!isStudentsSubdomain && !isScramble && <EmergencyBanner />}
      {!isStudentsSubdomain && !isScramble && !inClassroom && <JoinRequestWatcher />}
      <ErrorBoundary>
        <Routes>
          {isStudentsSubdomain ? (
            /* ── Student subdomain ── */
            <Route element={<StudentLayout />}>
              <Route path="/" element={<StudentLogIn />} />
              <Route path="/student-login" element={<StudentLogIn />} />
              {/* StudentLayout already renders StudentNavBar — don't double it. */}
              <Route path="/student-dashboard" element={<StudentDashboard />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          ) : inClassroom ? (
            /* ── Classroom Mode: only kid-safe surfaces; everything else → Sign-Out ── */
            <>
              <Route path="/" element={<ClassSignOutPage />} />
              <Route path="/classsignoutpage" element={<ClassSignOutPage />} />
              <Route path="/attendance" element={<Attendance />} />
              <Route path="/attendancerecord" element={<AttendanceRecord />} />
              <Route path="/library" element={<LibraryPage />} />
              <Route path="/lending" element={<LendingPage />} />
              <Route path="*" element={<Navigate to="/classsignoutpage" replace />} />
            </>
          ) : (
            /* ── Normal teacher app ── */
            <>
              {/* Authenticated Routes */}
              {userLoggedIn && needsOnboarding ? (
                /* Mid-onboarding: keep them in the wizard (/getstarted, a public
                   route below) until they finish — every other in-app path
                   redirects there. Resumable across reloads / navigation. */
                <Route path="*" element={<Navigate to="/getstarted" replace />} />
              ) : userLoggedIn ? (
                <>
                  {/* Management routes — wrapped in AuthenticatedLayout (centered,
                      padded content). Nav is the hamburger drawer, same as everywhere. */}
                  <Route element={<AuthenticatedLayout />}>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/displayclasses" element={<DisplayClasses />} />
                    <Route path="/invitations" element={<Invitations />} />
                    <Route
                      path="/displayenrolledstudents"
                      element={<DisplayEnrolledStudents />}
                    />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/addclass" element={<AddClass />} />
                    <Route path="/editclass/:classId" element={<AddClass />} />
                    <Route path="/importclasses" element={<ImportClasses />} />
                    <Route
                      path="/signoutsteacherrecord"
                      element={<SignOutsTeacherRecord />}
                    />
                    <Route path="/archivedclasses" element={<ArchivedClasses />} />
                    <Route path="/attendanceviewer" element={<AttendanceViewer />} />
                    <Route path="/schoolview" element={<SchoolView />} />
                    <Route path="/schooladmin" element={<SchoolAdmin />} />
                    <Route path="/schoolroster" element={<SchoolRoster />} />
                    <Route path="/studentinsights" element={<StudentInsights />} />
                  </Route>

                  {/* Full-width daily screens (no layout wrapper) */}
                  <Route path="/attendance" element={<Attendance />} />
                  <Route path="/attendancerecord" element={<AttendanceRecord />} />
                  <Route path="/emergency" element={<EmergencyView />} />
                  <Route path="/classsignoutpage" element={<ClassSignOutPage />} />
                  <Route path="/schoollist" element={<SchoolList />} />
                  <Route path="/lending" element={<LendingPage />} />
                  <Route path="/library" element={<LibraryPage />} />
                </>
              ) : (
                /* Don't redirect to login until auth has actually resolved —
                   otherwise a logged-in user gets bounced on first load. */
                <Route
                  path="*"
                  element={authLoading
                    ? <div className="checklist-page"><p>Loading…</p></div>
                    : <Navigate to="/loginform" replace />}
                />
              )}

              {/* Public Routes. Logged-in users hitting the root go straight to
                  the sign-out page rather than the marketing/landing page. */}
              <Route
                path="/"
                element={
                  userLoggedIn
                    ? <Navigate to={needsOnboarding ? "/getstarted" : "/classsignoutpage"} replace />
                    : <Home />
                }
              />
              <Route path="/adminpanel" element={<AdminPanel />} />
              <Route path="/about" element={<About />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/support" element={<Support />} />
              <Route path="/register" element={<RegistrationForm />} />
              <Route path="/loginform" element={<LogInForm />} />
              {/* The post-registration setup wizard (also the target of the
                  mid-onboarding redirect above). Kept as a public route so it
                  resolves in both the onboarding and normal branches. */}
              <Route path="/getstarted" element={<OnboardingWizard />} />
              <Route
                path="/afterregistrationschoolselection"
                element={<Navigate to="/getstarted" replace />}
              />
              <Route path="/signout/:id" element={<PublicSignOutPage />} />
              {/* Standalone golf Scramble scorecard — public, account-free,
                  unlisted. Explicit paths outrank the "*" login redirect above,
                  so these resolve for logged-out visitors (same as /signout). */}
              <Route path="/scramble/new" element={<ScrambleNew />} />
              <Route path="/scramble/:slug" element={<ScramblePage />} />
              {/* Catch-All Route */}
              <Route path="*" element={<h1>404 Not Found</h1>} />
            </>
          )}
        </Routes>
      </ErrorBoundary>
    </div>
    </TourProvider>
  );
}

// Student Layout with StudentNavBar
const StudentLayout = () => {
  const auth = getAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) navigate("/student-login");
    });
    return () => unsubscribe();
  }, [auth, navigate]);

  return (
    <div className="student-layout">
      <StudentNavBar />
      <div className="student-content">
        <Outlet />
      </div>
    </div>
  );
};

// Authenticated layout — centers/pads management pages (no in-flow sidebar).
const AuthenticatedLayout = () => {
  // No in-flow sidebar anymore — the app nav is the off-canvas drawer at every
  // width (opened from the navbar hamburger). This layout just centers/pads the
  // management pages' content.
  return (
    <div className="authenticated-layout">
      <div className="authenticated-content">
        <Outlet />
      </div>
    </div>
  );
};

export default App;
